import {
  FinalReleasePmiInspectionUnit,
  FinalReleasePmiRequirement,
  PmiScanResult,
  PmiUnit,
} from '../services/api';

export interface PmiDisplayState {
  completed: number;
  currentIndex: number;
  grade: string;
  remaining: number;
  serial: string;
  stateText: string;
  taskNo: string;
  total: number;
  unitLabel: string;
  waiting: boolean;
}

export interface PmiRequirementPoolItem {
  batchNo: string;
  completed: number;
  currentQuantity: number;
  customerCode: string;
  customerTechSpecId: string;
  drawingNo: string;
  failedCount: number;
  grade: string;
  itemNo: string;
  key: string;
  materialNo: string;
  orderNo: string;
  orderQuantity: number;
  passedCount: number;
  pmiDataText: string;
  pmiInspection: string;
  productDescription: string;
  requirementId: string;
  requirementText: string;
  scanCode: string;
  statusText: string;
  statusTone: PmiRequirementPoolStatusTone;
  taskNo: string;
  total: number;
}

export type PmiRequirementPoolStatusTone = 'bound' | 'cancelled' | 'complete' | 'partial' | 'pending';

function asString(value: unknown) {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return '';
}

function asNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  return 0;
}

function getUnitSerial(unit?: PmiUnit | null) {
  return asString(unit?.inspection_serial || unit?.serial_no || unit?.serial);
}

function normalizeSerial(value: unknown) {
  return asString(value).trim().toUpperCase();
}

function matchesUnitSerial(unit: PmiUnit, serial: string) {
  const target = normalizeSerial(serial);
  return Boolean(target && normalizeSerial(getUnitSerial(unit)) === target);
}

function toAwaitingUnit(unit: PmiUnit): PmiUnit {
  const nextUnit = { ...unit };
  delete nextUnit.bind_at;
  delete nextUnit.bound_at;
  delete nextUnit.spectrum_id;
  delete nextUnit.spectrum_file_id;
  delete nextUnit.spectrum_result_id;
  return {
    ...nextUnit,
    awaiting_spectrum: true,
    bind_status: 'pending',
    has_spectrum: false,
    is_bound: false,
    status: 'pending',
  };
}

function getUnitGrade(unit?: PmiUnit | null) {
  return asString(unit?.grade || unit?.material_grade || unit?.steel_grade);
}

function getTaskNo(result: PmiScanResult, currentUnit?: PmiUnit | null) {
  return asString(
    result.task_no
      || result.task?.task_no
      || result.task?.pmi_task_no
      || currentUnit?.task_no
      || currentUnit?.pmi_task_no,
  );
}

function getUnitLabel(unit: PmiUnit | undefined, index: number) {
  return asString(unit?.position_no || unit?.unit_no || unit?.piece_no || unit?.seq_no) || `第 ${index} 件`;
}

function hasBoundSpectrum(unit: PmiUnit | undefined) {
  if (!unit) {
    return false;
  }

  const status = asString(unit.status).toLowerCase();
  const bindStatus = asString(unit.bind_status).toLowerCase();
  return Boolean(
    unit.is_bound
      || unit.has_spectrum
      || unit.bind_at
      || unit.bound_at
      || unit.spectrum_id
      || unit.spectrum_file_id
      || unit.spectrum_result_id
      || ['bound', 'done', 'completed', 'finished'].includes(bindStatus)
      || ['bound', 'done', 'completed', 'finished'].includes(status),
  );
}

function getExplicitCompletedCount(units: PmiUnit[]) {
  return units.reduce((sum, unit) => sum + (hasBoundSpectrum(unit) ? 1 : 0), 0);
}

export function buildPmiDisplayState(result: PmiScanResult | null): PmiDisplayState | null {
  if (!result?.current_unit) {
    return null;
  }

  const units = Array.isArray(result.units) ? result.units : [];
  const currentUnit = result.current_unit;
  const serial = getUnitSerial(currentUnit);
  const currentIndex = Math.max(
    units.findIndex((unit) => getUnitSerial(unit) === serial) + 1,
    asNumber(currentUnit.index) || asNumber(currentUnit.seq_no) || 1,
  );
  const total = units.length || asNumber(result.total_units) || 1;
  const explicitCompleted = getExplicitCompletedCount(units);
  const waiting = currentUnit.awaiting_spectrum !== false && !hasBoundSpectrum(currentUnit);
  const completed = explicitCompleted || Math.max(waiting ? currentIndex - 1 : currentIndex, 0);
  const normalizedCompleted = Math.min(completed, total);

  return {
    completed: normalizedCompleted,
    currentIndex,
    grade: getUnitGrade(currentUnit) || asString(result.grade || result.material_grade),
    remaining: Math.max(total - normalizedCompleted, 0),
    serial,
    stateText: waiting ? '等待光谱' : '已完成',
    taskNo: getTaskNo(result, currentUnit),
    total,
    unitLabel: getUnitLabel(currentUnit, currentIndex),
    waiting,
  };
}

export function getPmiUnitSerial(unit?: PmiUnit | null) {
  return getUnitSerial(unit);
}

export function selectPmiScanResultUnit(
  result: PmiScanResult | null,
  serial: string,
  options?: { forceAwaiting?: boolean },
): PmiScanResult | null {
  if (!result || !Array.isArray(result.units)) {
    return null;
  }

  const selectedUnit = result.units.find((unit) => matchesUnitSerial(unit, serial));
  if (!selectedUnit) {
    return null;
  }

  const selectedDisplayUnit = options?.forceAwaiting || !hasBoundSpectrum(selectedUnit)
    ? toAwaitingUnit(selectedUnit)
    : selectedUnit;
  return {
    ...result,
    current_unit: selectedDisplayUnit,
    scan_type: 'unit',
    units: result.units.map((unit) => (matchesUnitSerial(unit, serial) ? selectedDisplayUnit : unit)),
  };
}

function getRequirementId(record: FinalReleasePmiRequirement) {
  return asString(record.id || record.pmi_requirement_id || record.requirement_id);
}

function getRequirementScanId(record: FinalReleasePmiRequirement) {
  return asString(record.inspection_plan_id || record.pmi_task_id || record.id || record.inspection_item_id);
}

function getRequirementTaskQrCode(record: FinalReleasePmiRequirement) {
  const rawRecord = record as Record<string, unknown>;
  return asString(
    rawRecord.pmi_task_no
      || rawRecord.pmiTaskNo
      || rawRecord.task_no
      || rawRecord.taskNo,
  );
}

function getRequirementTaskNo(record: FinalReleasePmiRequirement, planId: string) {
  const taskQrCode = getRequirementTaskQrCode(record);
  return asString(record.inspection_batch_no || record.sy_no || taskQrCode || record.order_no) || `PMI_TASK:${planId}`;
}

function normalizePmiDataText(value: unknown) {
  const text = asString(value).trim();
  if (text === '绑定') {
    return '已绑定';
  }
  return text;
}

function getRequirementStatus(status: unknown, completed: number, total: number, pmiDataText: string): {
  text: string;
  tone: PmiRequirementPoolStatusTone;
} {
  if (completed > 0 && completed < total) {
    return { text: '部分检测', tone: 'partial' };
  }

  if (pmiDataText) {
    return {
      text: pmiDataText,
      tone: pmiDataText.includes('绑') ? 'bound' : 'pending',
    };
  }

  if (completed > 0 && completed >= total) {
    return { text: '已完成', tone: 'complete' };
  }

  const value = asString(status).toLowerCase();
  if (value === 'completed' || value === 'done') {
    return { text: '已完成', tone: 'complete' };
  }
  if (value === 'cancelled' || value === 'canceled') {
    return { text: '已取消', tone: 'cancelled' };
  }
  return { text: '待检测', tone: 'pending' };
}

export function buildPmiRequirementPoolItem(record: FinalReleasePmiRequirement): PmiRequirementPoolItem | null {
  const requirementId = getRequirementId(record);
  const scanId = getRequirementScanId(record);
  if (!requirementId) {
    return null;
  }

  const passed = asNumber(record.passed_count);
  const failed = asNumber(record.failed_count);
  const completed = passed + failed;
  const currentQuantity = asNumber(record.current_quantity);
  const orderQuantity = asNumber(record.order_quantity);
  const total = currentQuantity || orderQuantity || Math.max(completed, 1);
  const pmiDataText = normalizePmiDataText(record.pmi_data);
  const status = getRequirementStatus(record.status, completed, total, pmiDataText);
  const taskQrCode = getRequirementTaskQrCode(record);

  return {
    batchNo: asString(record.inspection_batch_no),
    completed,
    currentQuantity,
    customerCode: asString(record.customer_code),
    customerTechSpecId: asString(record.customer_tech_spec_id),
    drawingNo: asString(record.drawing_no),
    failedCount: failed,
    grade: asString(record.material || record.material_grade),
    itemNo: asString(record.item_no || record.line_no),
    key: requirementId,
    materialNo: asString(record.material_no),
    orderNo: asString(record.order_no),
    orderQuantity,
    passedCount: passed,
    pmiDataText,
    pmiInspection: asString(record.pmi_inspection),
    productDescription: asString(record.product_description),
    requirementId,
    requirementText: asString(record.material_requirement),
    scanCode: taskQrCode || (scanId ? `PMI_TASK:${scanId}` : ''),
    statusText: status.text,
    statusTone: status.tone,
    taskNo: getRequirementTaskNo(record, scanId || requirementId),
    total,
  };
}

function extractRequirementUnits(data?: {
  items?: FinalReleasePmiInspectionUnit[];
  list?: FinalReleasePmiInspectionUnit[];
  data?: FinalReleasePmiInspectionUnit[];
}) {
  const items = data?.items;
  const list = data?.list;
  const nestedData = data?.data;

  if (Array.isArray(items)) {
    return items;
  }
  if (Array.isArray(list)) {
    return list;
  }
  if (Array.isArray(nestedData)) {
    return nestedData;
  }
  return [];
}

function isUnitBound(unit: FinalReleasePmiInspectionUnit) {
  const bindStatus = asString(unit.bind_status).toLowerCase();
  const finalResult = asString(unit.final_result).toLowerCase();
  return Boolean(
    unit.bound_at
      || unit.replicate_id
      || ['bound', 'done', 'completed', 'finished'].includes(bindStatus)
      || ['pass', 'fail', 'passed', 'failed'].includes(finalResult),
  );
}

export function buildPmiScanResultFromRequirementUnits(
  meta: {
    grade?: string;
    pmiInspection?: string;
    requirementId: string;
    taskNo: string;
  },
  data?: {
    items?: FinalReleasePmiInspectionUnit[];
    list?: FinalReleasePmiInspectionUnit[];
    data?: FinalReleasePmiInspectionUnit[];
  } | FinalReleasePmiInspectionUnit[],
  selectedSerial?: string,
): PmiScanResult {
  const rawUnits = Array.isArray(data) ? data : extractRequirementUnits(data);
  const units = rawUnits.map<PmiUnit>((unit, index) => {
    const unitSeq = asNumber(unit.unit_seq) || index + 1;
    const bound = isUnitBound(unit);

    return {
      ...unit,
      awaiting_spectrum: !bound,
      bind_at: asString(unit.bound_at),
      bind_status: asString(unit.bind_status) || (bound ? 'bound' : 'pending'),
      bound_at: asString(unit.bound_at),
      elements: unit.elements,
      final_result: unit.final_result,
      grade: asString(unit.grade || meta.grade),
      index: unitSeq,
      inspection_serial: asString(unit.inspection_serial) || `${meta.taskNo}-${unitSeq}`,
      piece_no: asString(unit.product_sn),
      pmi_inspection: asString(unit.pmi_inspection) || asString(meta.pmiInspection),
      position_no: `第 ${unitSeq} 件`,
      raw_status: unit.raw_status,
      seq_no: unitSeq,
      status: bound ? 'bound' : 'pending',
    };
  });
  const selectedUnit = selectedSerial
    ? units.find((unit) => matchesUnitSerial(unit, selectedSerial))
    : undefined;
  const currentUnit = selectedUnit || units.find((unit) => !hasBoundSpectrum(unit)) || units[0] || {
    awaiting_spectrum: true,
    grade: meta.grade,
    inspection_serial: meta.taskNo || meta.requirementId,
    position_no: '第 1 件',
    seq_no: 1,
    status: 'pending',
  };

  return {
    current_unit: {
      ...currentUnit,
      awaiting_spectrum: currentUnit.awaiting_spectrum ?? !hasBoundSpectrum(currentUnit),
    },
    grade: meta.grade,
    scan_type: 'task',
    task: {
      id: meta.requirementId,
      material_grade: meta.grade,
      task_no: meta.taskNo,
    },
    task_no: meta.taskNo,
    total_units: units.length || 1,
    units: units.length ? units : [currentUnit],
  };
}
